import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { UserProfile } from './types';
import { useAppRouter } from './hooks/useAppRouter';
import { resolveAppView, getTermsInitialTab } from './utils/router';
import { trackAppError } from './utils/analytics';
import { lazyWithRetry } from './utils/lazyWithRetry';
import CookieConsentBanner from './components/CookieConsentBanner';
import { AuthWorkspaceProvider } from './context/AuthWorkspaceContext';
import { AuthProvider } from './context/AuthContext';
import { CompanyProvider } from './context/CompanyContext';
import {
  recordActivity,
  forceRecordActivity,
  isSessionExpired,
  clearSessionActivity
} from './utils/sessionInactivity';

// Error boundary
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    (this as any).state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    trackAppError(error, errorInfo.componentStack || 'React.ErrorBoundary');

    const msg = String(error?.message || error || '');
    if (
      msg.includes('dynamically imported module') ||
      msg.includes('Failed to fetch') ||
      msg.includes('Loading chunk') ||
      msg.includes('error loading dynamically imported module')
    ) {
      const lastReload = parseInt(sessionStorage.getItem('chunk_reload_ts') || '0', 10);
      if (Date.now() - lastReload > 10000) {
        sessionStorage.setItem('chunk_reload_ts', Date.now().toString());
        window.location.reload();
      }
    }
  }

  render() {
    if ((this as any).state?.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] p-6 text-center">
          <div className="max-w-md bg-white p-8 rounded-3xl border border-slate-200 shadow-sm space-y-4">
            <div className="h-12 w-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto text-amber-600 font-bold text-lg">
              !
            </div>
            <h2 className="text-lg font-bold text-slate-800">Siden kunne ikke indlæses helt</h2>
            <p className="text-xs text-slate-500 leading-relaxed">
              Der opstod en midlertidig indlæsningsfejl. Klik på knappen nedenfor for at genindlæse modulet.
            </p>
            <button
              onClick={() => {
                sessionStorage.removeItem('chunk_retry_refreshed');
                window.location.reload();
              }}
              className="px-5 py-2.5 bg-[#0050ff] text-white font-semibold text-xs rounded-xl hover:bg-blue-600 transition-all cursor-pointer shadow-sm"
            >
              Genindlæs siden
            </button>
          </div>
        </div>
      );
    }
    return (this as any).props.children;
  }
}

// Lazy-loaded Public Pages (Code-split for instant landing performance)
const KvikSignLandingPage = lazyWithRetry(() => import('./components/KvikSignLandingPage'));
const PricingPage = lazyWithRetry(() => import('./components/PricingPage'));
const FeaturesOverviewPage = lazyWithRetry(() => import('./components/FeaturesOverviewPage'));
const KvikSignFeaturePage = lazyWithRetry(() => import('./components/KvikSignFeaturePage'));
const KvikIDFeaturePage = lazyWithRetry(() => import('./components/KvikIDFeaturePage'));
const KvikSendFeaturePage = lazyWithRetry(() => import('./components/KvikSendFeaturePage'));
const KvikFormFeaturePage = lazyWithRetry(() => import('./components/KvikFormFeaturePage'));
const TermsPage = lazyWithRetry(() => import('./components/TermsPage'));
const ContactUsPage = lazyWithRetry(() => import('./components/ContactUsPage'));
const ArticlesListPage = lazyWithRetry(() => import('./components/ArticlesListPage'));
const ArticleDetailPage = lazyWithRetry(() => import('./components/ArticleDetailPage'));
const IndustriesOverviewPage = lazyWithRetry(() => import('./components/IndustriesOverviewPage'));
const IndustryDetailPage = lazyWithRetry(() => import('./components/IndustryDetailPage'));
const IntegrationsOverviewPage = lazyWithRetry(() => import('./components/IntegrationsOverviewPage'));
const IntegrationDetailPage = lazyWithRetry(() => import('./components/IntegrationDetailPage'));
const Login = lazyWithRetry(() => import('./components/Login'));
const SetPasswordOnboarding = lazyWithRetry(() => import('./components/SetPasswordOnboarding'));
const ResetPasswordPage = lazyWithRetry(() => import('./components/ResetPasswordPage'));
const SignSignatureRoom = lazyWithRetry(() => import('./components/SignatureRoom'));
const PublicFormRunner = lazyWithRetry(() => import('./components/PublicFormRunner'));

// Lazy-loaded Authenticated Workspace (Entire internal platform loaded only on login)
const AppWorkspace = lazyWithRetry(() => import('./components/AppWorkspace'));

// Minimalist Non-blocking Page Loader
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
    <div className="space-y-4 text-center animate-fade-in">
      <div className="h-8 w-8 border-3 border-[#0050ff] border-t-transparent rounded-full animate-spin mx-auto" />
      <p className="text-xs font-semibold text-slate-400 font-mono">Indlæser...</p>
    </div>
  </div>
);

export default function App() {
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Custom URL/Path routing hook
  const { currentPath, setCurrentPath, navigateTo } = useAppRouter();

  // Listen to Firebase Auth state change eagerly and seamlessly
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let isMounted = true;

    const startAuth = async () => {
      try {
        const [{ auth }, { onAuthStateChanged }, { ensureUserProfile }] = await Promise.all([
          import('./firebase'),
          import('firebase/auth'),
          import('./services/dbService')
        ]);

        if (!isMounted) return;

        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (!isMounted) return;
          if (user) {
            // Enforce 24-hour session inactivity timeout
            if (isSessionExpired()) {
              clearSessionActivity();
              try {
                localStorage.removeItem('kviksign_has_session');
              } catch (_) {}
              try {
                const { signOut } = await import('firebase/auth');
                await signOut(auth);
              } catch (_) {}
              if (isMounted) {
                setCurrentUser(null);
                setAuthLoading(false);
                const currentPathLower = (window.location.pathname || '/').trim().toLowerCase();
                const isPublicRoute = (
                  currentPathLower.startsWith('/f/') ||
                  currentPathLower.startsWith('/form/') ||
                  currentPathLower.startsWith('/forms/') ||
                  currentPathLower.startsWith('/sign/') ||
                  currentPathLower.startsWith('/s/') ||
                  currentPathLower.startsWith('/signature') ||
                  currentPathLower === '/' ||
                  currentPathLower === '/da' ||
                  currentPathLower === '/en' ||
                  currentPathLower === '/sv' ||
                  currentPathLower === '/se' ||
                  currentPathLower.startsWith('/terms') ||
                  currentPathLower.startsWith('/betingelser') ||
                  currentPathLower.startsWith('/privacy') ||
                  currentPathLower.startsWith('/privatliv') ||
                  currentPathLower.startsWith('/contact') ||
                  currentPathLower.startsWith('/kontakt') ||
                  currentPathLower.startsWith('/features') ||
                  currentPathLower.startsWith('/funktioner') ||
                  currentPathLower.startsWith('/pricing') ||
                  currentPathLower.startsWith('/priser') ||
                  currentPathLower.startsWith('/brancher') ||
                  currentPathLower.startsWith('/industries') ||
                  currentPathLower.startsWith('/integration') ||
                  currentPathLower.startsWith('/artikler') ||
                  currentPathLower.startsWith('/articles') ||
                  currentPathLower.startsWith('/kvik')
                );
                if (!isPublicRoute) {
                  navigateTo('/login?reason=inactivity');
                }
              }
              return;
            }

            // Session is active; record/refresh activity timestamp
            forceRecordActivity();
            try {
              localStorage.setItem('kviksign_has_session', 'true');
            } catch (_) {}

            try {
              const profile = await ensureUserProfile(user.uid, user.email || '', user.displayName);
              if (isMounted) {
                setCurrentUser(profile);
                // If user is logged in and visits root "/" or login/signup, auto-redirect directly to dashboard
                const rawPath = (window.location.pathname || '/').trim().toLowerCase();
                if (
                  rawPath === '/' || 
                  rawPath === '/da' || 
                  rawPath === '/en' || 
                  rawPath === '/sv' || 
                  rawPath === '/se' || 
                  rawPath === '/login' || 
                  rawPath === '/signup' || 
                  rawPath === '/da/login' || 
                  rawPath === '/da/signup' || 
                  rawPath === '/en/login' || 
                  rawPath === '/en/signup' || 
                  rawPath === '/sv/login' || 
                  rawPath === '/sv/signup' || 
                  rawPath === '/se/login' || 
                  rawPath === '/se/signup'
                ) {
                  navigateTo('/app/sign/dashboard');
                }
              }
            } catch (err) {
              console.error("Fejl ved synkronisering af brugerprofil:", err);
              try {
                localStorage.removeItem('kviksign_has_session');
                await auth.signOut();
              } catch (_) {}
              if (isMounted) setCurrentUser(null);
            }
          } else {
            clearSessionActivity();
            try {
              localStorage.removeItem('kviksign_has_session');
            } catch (_) {}
            if (isMounted) setCurrentUser(null);
          }
          if (isMounted) setAuthLoading(false);
        });
      } catch (e) {
        console.error("Fejl ved indlæsning af auth:", e);
        if (isMounted) setAuthLoading(false);
      }
    };

    startAuth();

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [navigateTo]);

  const handleNavigate = useCallback(async (path: string) => {
    navigateTo(path);
  }, [navigateTo]);

  // 24-hour session inactivity tracker
  useEffect(() => {
    if (!currentUser) return;

    forceRecordActivity();

    const handleUserInteraction = () => {
      recordActivity();
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((evt) => {
      window.addEventListener(evt, handleUserInteraction, { passive: true });
    });

    // Check periodically (once every hour) if 24 hours of inactivity has elapsed
    const ONE_HOUR_MS = 60 * 60 * 1000;
    const intervalId = setInterval(async () => {
      if (isSessionExpired()) {
        clearSessionActivity();
        try {
          localStorage.removeItem('kviksign_has_session');
        } catch (_) {}
        try {
          const [{ auth }, { signOut }] = await Promise.all([
            import('./firebase'),
            import('firebase/auth')
          ]);
          await signOut(auth);
        } catch (e) {
          console.error("Fejl ved automatisk udlogning pga. inaktivitet:", e);
        }
        setCurrentUser(null);
        const currentPathLower = (window.location.pathname || '/').trim().toLowerCase();
        const isPublicRoute = (
          currentPathLower.startsWith('/f/') ||
          currentPathLower.startsWith('/form/') ||
          currentPathLower.startsWith('/forms/') ||
          currentPathLower.startsWith('/sign/') ||
          currentPathLower.startsWith('/s/') ||
          currentPathLower.startsWith('/signature') ||
          currentPathLower === '/' ||
          currentPathLower === '/da' ||
          currentPathLower === '/en' ||
          currentPathLower === '/sv' ||
          currentPathLower === '/se' ||
          currentPathLower.startsWith('/terms') ||
          currentPathLower.startsWith('/betingelser') ||
          currentPathLower.startsWith('/privacy') ||
          currentPathLower.startsWith('/privatliv') ||
          currentPathLower.startsWith('/contact') ||
          currentPathLower.startsWith('/kontakt') ||
          currentPathLower.startsWith('/features') ||
          currentPathLower.startsWith('/funktioner') ||
          currentPathLower.startsWith('/pricing') ||
          currentPathLower.startsWith('/priser') ||
          currentPathLower.startsWith('/brancher') ||
          currentPathLower.startsWith('/industries') ||
          currentPathLower.startsWith('/integration') ||
          currentPathLower.startsWith('/artikler') ||
          currentPathLower.startsWith('/articles') ||
          currentPathLower.startsWith('/kvik')
        );
        if (!isPublicRoute) {
          navigateTo('/login?reason=inactivity');
        }
      }
    }, ONE_HOUR_MS);

    return () => {
      events.forEach((evt) => {
        window.removeEventListener(evt, handleUserInteraction);
      });
      clearInterval(intervalId);
    };
  }, [currentUser, navigateTo]);

  const handleLoginSuccess = async () => {
    try {
      forceRecordActivity();
      try {
        localStorage.setItem('kviksign_has_session', 'true');
      } catch (_) {}
      const [{ auth }, { ensureUserProfile }] = await Promise.all([
        import('./firebase'),
        import('./services/dbService')
      ]);
      if (auth.currentUser) {
        const profile = await ensureUserProfile(auth.currentUser.uid, auth.currentUser.email || '', auth.currentUser.displayName);
        setCurrentUser(profile);
      }
      handleNavigate('/app/sign/dashboard');
    } catch (err) {
      console.error("Fejl ved login succes profil-initialisering:", err);
      handleNavigate('/app/sign/dashboard');
    }
  };

  const handleLogout = async () => {
    try {
      clearSessionActivity();
      try {
        localStorage.removeItem('kviksign_has_session');
      } catch (_) {}
      const [{ auth }, { signOut }] = await Promise.all([
        import('./firebase'),
        import('firebase/auth')
      ]);
      await signOut(auth);
      setCurrentUser(null);
      handleNavigate('/login');
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Inspect signature room or public forms
  const isSignatureRoom = (
    currentPath.startsWith('/sign/') ||
    currentPath.startsWith('/s/') ||
    currentPath.startsWith('/signature/') ||
    currentPath.startsWith('/signature-room') ||
    currentPath.startsWith('/sign-success') ||
    currentPath.startsWith('/sign-error')
  );

  const view = resolveAppView({
    currentPath,
    currentUser,
    hasSigningScope: true,
    isSignatureRoom,
  });

  const renderContent = () => {
    // If user previously had an active session and auth is still verifying in the background,
    // show a clean page loader instead of momentarily flashing the public marketing landing page
    const hadActiveSession = typeof window !== 'undefined' && localStorage.getItem('kviksign_has_session') === 'true';
    if (authLoading && hadActiveSession && (view === 'landing' || currentPath === '/' || currentPath.startsWith('/app'))) {
      return <PageLoader />;
    }

    // --- 1. SIGNATURE ROOM & PUBLIC FORM RUNNER (Standalone full-screen views) ---
  if (view === 'signature-room') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <SignSignatureRoom onNavigate={handleNavigate} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (view === 'public-form') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <PublicFormRunner currentPath={currentPath} onNavigate={handleNavigate} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // --- 2. PUBLIC MARKETING & SALES PAGES (Zero auth-wait latency for maximum speed) ---
  if (view === 'landing') {
    return (
      <Suspense fallback={<PageLoader />}>
        <KvikSignLandingPage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'priser') {
    return (
      <Suspense fallback={<PageLoader />}>
        <PricingPage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'funktioner') {
    return (
      <Suspense fallback={<PageLoader />}>
        <FeaturesOverviewPage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'kviksign') {
    return (
      <Suspense fallback={<PageLoader />}>
        <KvikSignFeaturePage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'kvikid') {
    return (
      <Suspense fallback={<PageLoader />}>
        <KvikIDFeaturePage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'kviksend') {
    return (
      <Suspense fallback={<PageLoader />}>
        <KvikSendFeaturePage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'kvikform') {
    return (
      <Suspense fallback={<PageLoader />}>
        <KvikFormFeaturePage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'betingelser') {
    return (
      <Suspense fallback={<PageLoader />}>
        <TermsPage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
          initialTab={getTermsInitialTab()}
        />
      </Suspense>
    );
  }

  if (view === 'privatlivspolitik') {
    return (
      <Suspense fallback={<PageLoader />}>
        <TermsPage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
          initialTab="privatliv"
        />
      </Suspense>
    );
  }

  if (view === 'kontakt-os') {
    return (
      <Suspense fallback={<PageLoader />}>
        <ContactUsPage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'brancher') {
    return (
      <Suspense fallback={<PageLoader />}>
        <IndustriesOverviewPage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'branche-detail') {
    const slug = currentPath
      .replace(/^\/(dk|da|eng|en|sv|se)\//, '/')
      .replace('/industries/', '')
      .replace('/brancher/', '')
      .replace('/branche/', '')
      .replace('/branscher/', '')
      .replace(/^\//, '')
      .split('?')[0]
      .split('#')[0];
    return (
      <Suspense fallback={<PageLoader />}>
        <IndustryDetailPage 
          slug={slug}
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'integrationer') {
    return (
      <Suspense fallback={<PageLoader />}>
        <IntegrationsOverviewPage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'integration-detail') {
    const slug = currentPath
      .replace(/^\/(dk|da|eng|en|sv|se)\//, '/')
      .replace('/integrations/', '')
      .replace('/integrationer/', '')
      .replace('/integration/', '')
      .replace(/^\//, '')
      .split('?')[0]
      .split('#')[0];
    return (
      <Suspense fallback={<PageLoader />}>
        <IntegrationDetailPage 
          slug={slug}
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'artikler') {
    return (
      <Suspense fallback={<PageLoader />}>
        <ArticlesListPage 
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  if (view === 'artikel-detail') {
    const slug = currentPath
      .replace(/^\/(dk|da|eng|en|sv|se)\//, '/')
      .replace('/articles/', '')
      .replace('/artikler/', '')
      .replace('/viden/', '')
      .replace('/blog/', '')
      .replace(/^\//, '')
      .split('?')[0]
      .split('#')[0];
    return (
      <Suspense fallback={<PageLoader />}>
        <ArticleDetailPage 
          slug={slug}
          onNavigate={handleNavigate}
          isAuthenticated={!!currentUser}
        />
      </Suspense>
    );
  }

  // --- 3. LOGIN & SIGNUP PAGES ---
  if (view === 'login') {
    const rawPath = currentPath.split('?')[0].split('#')[0].toLowerCase();
    const isSignup = rawPath.endsWith('/signup') || rawPath.includes('/signup');
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Login 
            onLoginSuccess={handleLoginSuccess} 
            initialRegistering={isSignup} 
            onNavigate={handleNavigate}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (view === 'set-password') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <SetPasswordOnboarding 
            onLoginSuccess={handleLoginSuccess} 
            onNavigate={handleNavigate}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (view === 'reset-password') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <ResetPasswordPage 
            onLoginSuccess={handleLoginSuccess} 
            onNavigate={handleNavigate}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // --- 4. AUTHENTICATED WORKSPACE ---
  // If still checking auth for an /app path, show sleek minimal spinner
  if (authLoading && currentPath.startsWith('/app')) {
    return <PageLoader />;
  }

  // If user is not logged in on an /app path, redirect to login
  if (!currentUser && currentPath.startsWith('/app')) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Login 
            onLoginSuccess={handleLoginSuccess} 
            initialRegistering={false} 
            onNavigate={handleNavigate}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // If user is logged in, render the full workspace
  if (currentUser) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <AuthWorkspaceProvider currentUser={currentUser} setCurrentUser={setCurrentUser}>
            <AuthProvider>
              <CompanyProvider>
                <AppWorkspace
                  currentUser={currentUser}
                  setCurrentUser={setCurrentUser}
                  currentPath={currentPath}
                  setCurrentPath={setCurrentPath}
                  handleNavigate={handleNavigate}
                  view={view}
                  onLogout={handleLogout}
                />
              </CompanyProvider>
            </AuthProvider>
          </AuthWorkspaceProvider>
        </Suspense>
      </ErrorBoundary>
    );
  }

    // Default fallback to public landing
    return (
      <Suspense fallback={<PageLoader />}>
        <KvikSignLandingPage 
          onNavigate={handleNavigate}
          isAuthenticated={false}
        />
      </Suspense>
    );
  };

  return (
    <>
      {renderContent()}
      <CookieConsentBanner onNavigate={handleNavigate} />
    </>
  );
}
